import { supabase } from '../../config/supabase';
import { Category, CategoryWithPreferences } from '../../types/category';
import logger from '../../utils/logger';
import { eventsService } from '../events.service';
import { notificationsService } from './notifications.service';
import { userCategoryPreferencesService } from './user-category-preferences.service';

// Types pour les entrées de création et mise à jour
// Ces types sont définis ici temporairement pour assurer la compatibilité
// avec le code existant, ils seront déplacés dans le fichier category.ts plus tard
export type CategoryCreateInput = Omit<Category, 'id'>;
export type CategoryUpdateInput = Partial<CategoryCreateInput>;

/**
 * Convertit un objet catégorie du format Supabase vers le format de l'application
 * @param data Données de la catégorie au format Supabase
 * @returns Catégorie au format de l'application
 */
function toAppFormat(data: any): Category {
  return {
    id: data.id,
    name: data.name,
    color: data.color,
    icon: data.icon,
    markerShape: data.marker_shape || 'circle',
    isVisible: data.is_visible !== undefined ? data.is_visible : true,
    createdAt: data.created_at ? new Date(data.created_at) : undefined,
    updatedAt: data.updated_at ? new Date(data.updated_at) : undefined
  };
}

/**
 * Convertit un objet catégorie du format de l'application vers le format Supabase
 * @param categoryData Données de la catégorie au format de l'application
 * @returns Données de la catégorie au format Supabase
 */
function toSupabaseFormat(categoryData: CategoryCreateInput): Record<string, any> {
  const result: Record<string, any> = {
    name: categoryData.name,
    color: categoryData.color,
    icon: categoryData.icon,
    marker_shape: categoryData.markerShape || 'circle'
  };
  
  if (categoryData.isVisible !== undefined) {
    result.is_visible = categoryData.isVisible;
  }
  
  logger.debug('toSupabaseFormat - Données envoyées:', result);
  
  return result;
}

/**
 * Service pour gérer les opérations CRUD sur les catégories dans Supabase
 */
export const categoriesService = {
  /**
   * Récupère toutes les catégories
   * @returns Une promesse qui résout avec un tableau de catégories
   */
  async getAll(): Promise<CategoryWithPreferences[]> {
    try {
      // Récupérer les catégories
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        logger.error('Erreur lors de la récupération des catégories:', error);
        throw error;
      }
      
      const categories = (data || []).map(toAppFormat);
      
      // Récupérer les préférences utilisateur
      const preferences = await userCategoryPreferencesService.getUserPreferences();
      const preferencesMap = new Map(preferences.map(p => [p.categoryId, p.displayOrder]));
      
      // Combiner les catégories avec leurs préférences
      const categoriesWithPrefs: CategoryWithPreferences[] = categories.map(cat => ({
        ...cat,
        displayOrder: preferencesMap.get(cat.id) ?? 999 // Les catégories sans préférence vont à la fin
      }));
      
      // Trier par displayOrder
      categoriesWithPrefs.sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
      
      return categoriesWithPrefs;
    } catch (error) {
      logger.error('Erreur lors de la récupération des catégories:', error);
      throw error;
    }
  },

  /**
   * Récupère une catégorie par son ID
   * @param id L'ID de la catégorie à récupérer
   * @returns Une promesse qui résout avec la catégorie ou null si non trouvée
   */
  async getById(id: string): Promise<Category | null> {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Code d'erreur pour "No rows found"
          return null;
        }
        logger.error(`Erreur lors de la récupération de la catégorie ${id}:`, error);
        throw error;
      }

      if (!data) return null;
      
      return toAppFormat(data);
    } catch (error) {
      logger.error(`Erreur lors de la récupération de la catégorie ${id}:`, error);
      throw error;
    }
  },

  /**
   * Crée une nouvelle catégorie
   * @param categoryData Les données de la catégorie à créer
   * @returns Une promesse qui résout avec la catégorie créée
   */
  async create(categoryData: CategoryCreateInput): Promise<Category> {
    try {
      // Validation des données
      if (!categoryData.name || !categoryData.color || !categoryData.icon) {
        throw new Error('Les champs name, color et icon sont obligatoires');
      }

      // Convertir les données au format Supabase
      const supabaseData = toSupabaseFormat(categoryData);

      const { data, error } = await supabase
        .from('categories')
        .insert([supabaseData])
        .select()
        .single();

      if (error) {
        logger.error('Erreur lors de la création de la catégorie:', error);
        throw error;
      }

      if (!data) {
        logger.error('La catégorie a été créée mais aucune donnée n\'a été retournée');
        throw new Error('La catégorie a été créée mais aucune donnée n\'a été retournée');
      }

      logger.info(`Catégorie créée avec succès: ${data.name} (${data.id})`);
      return toAppFormat(data);
    } catch (error) {
      logger.error('Erreur lors de la création de la catégorie:', error);
      throw error;
    }
  },

  /**
   * Met à jour une catégorie existante
   * @param id L'ID de la catégorie à mettre à jour
   * @param categoryData Les données à mettre à jour
   * @returns Une promesse qui résout avec la catégorie mise à jour
   */
  async update(id: string, categoryData: CategoryUpdateInput): Promise<Category> {
    try {
      // Vérifier si la catégorie existe
      const existingCategory = await this.getById(id);
      if (!existingCategory) {
        logger.error(`La catégorie avec l'ID ${id} n'existe pas`);
        throw new Error(`La catégorie avec l'ID ${id} n'existe pas`);
      }

      // Convertir les données au format Supabase
      const supabaseData: Record<string, any> = {};
      
      if (categoryData.name !== undefined) supabaseData.name = categoryData.name;
      if (categoryData.color !== undefined) supabaseData.color = categoryData.color;
      if (categoryData.icon !== undefined) supabaseData.icon = categoryData.icon;
      if (categoryData.markerShape !== undefined) supabaseData.marker_shape = categoryData.markerShape;

      const { data, error } = await supabase
        .from('categories')
        .update(supabaseData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error(`Erreur lors de la mise à jour de la catégorie ${id}:`, error);
        throw error;
      }

      if (!data) {
        logger.error('La catégorie a été mise à jour mais aucune donnée n\'a été retournée');
        throw new Error('La catégorie a été mise à jour mais aucune donnée n\'a été retournée');
      }

      logger.info(`Catégorie mise à jour avec succès: ${data.name} (${data.id})`);
      return toAppFormat(data);
    } catch (error) {
      logger.error(`Erreur lors de la mise à jour de la catégorie ${id}:`, error);
      throw error;
    }
  },

  /**
   * Supprime une catégorie et tous les marqueurs associés
   * @param id L'ID de la catégorie à supprimer
   * @returns Une promesse qui résout avec les IDs des marqueurs supprimés
   */
  async delete(id: string): Promise<string[]> {
    try {
      // Vérifier si la catégorie existe
      const existingCategory = await this.getById(id);
      if (!existingCategory) {
        logger.error(`La catégorie avec l'ID ${id} n'existe pas`);
        throw new Error(`La catégorie avec l'ID ${id} n'existe pas`);
      }

      // Log pour le débogage
      logger.debug(`Suppression de la catégorie ${existingCategory.name} (${id}) et de ses marqueurs associés`);

      // Vérifier les marqueurs associés à cette catégorie
      const { data: associatedMarkers, error: fetchError } = await supabase
        .from('markers')
        .select('id, address')
        .eq('category_id', id);

      if (fetchError) {
        logger.error(`Erreur lors de la récupération des marqueurs associés à la catégorie ${id}:`, fetchError);
        throw fetchError;
      }

      logger.debug(`Marqueurs associés à la catégorie ${id}:`, associatedMarkers);
      logger.info(`Nombre de marqueurs à supprimer: ${associatedMarkers?.length || 0}`);

      // Supprimer d'abord les marqueurs associés à cette catégorie
      const { data: deletedMarkers, error: markersError } = await supabase
        .from('markers')
        .delete()
        .eq('category_id', id)
        .select();

      if (markersError) {
        logger.error(`Erreur lors de la suppression des marqueurs associés à la catégorie ${id}:`, markersError);
        throw markersError;
      }

      logger.info(`Marqueurs supprimés: ${deletedMarkers?.length || 0}`, deletedMarkers);

      // Ensuite, supprimer la catégorie elle-même
      const { error: categoryError } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (categoryError) {
        logger.error(`Erreur lors de la suppression de la catégorie ${id}:`, categoryError);
        throw categoryError;
      }

      logger.info(`Catégorie supprimée avec succès: ${existingCategory.name} (${id})`);
      
      // Retourner les IDs des marqueurs supprimés pour mettre à jour l'état de l'application
      return deletedMarkers ? deletedMarkers.map(marker => marker.id) : [];
    } catch (error) {
      logger.error(`Erreur lors de la suppression de la catégorie ${id}:`, error);
      throw error;
    }
  },

  /**
   * Enregistre une action de changement de catégorie pour un marqueur
   * @param markerId L'ID du marqueur
   * @param oldCategoryId L'ID de l'ancienne catégorie
   * @param newCategoryId L'ID de la nouvelle catégorie
   * @returns Une promesse qui résout lorsque l'enregistrement est terminé
   */
  async recordCategoryChange(markerId: string, oldCategoryId: string, newCategoryId: string): Promise<void> {
    try {
      // Utiliser la fonction RPC pour enregistrer le changement de catégorie
      const { error } = await supabase.rpc('record_category_change', {
        marker_id: markerId,
        old_category_id: oldCategoryId,
        new_category_id: newCategoryId
      });

      if (error) {
        logger.error(`Erreur lors de l'enregistrement du changement de catégorie pour le marqueur ${markerId}:`, error);
        throw error;
      }
      
      // Récupérer les noms des catégories pour les notifications
      let oldCategoryName = 'Sans catégorie';
      let newCategoryName = 'Sans catégorie';
      
      if (oldCategoryId) {
        const { data: oldCat } = await supabase
          .from('categories')
          .select('name')
          .eq('id', oldCategoryId)
          .single();
          
        if (oldCat) oldCategoryName = oldCat.name;
      }
      
      if (newCategoryId) {
        const { data: newCat } = await supabase
          .from('categories')
          .select('name')
          .eq('id', newCategoryId)
          .single();
          
        if (newCat) newCategoryName = newCat.name;
      }
      
      // Mettre à jour les notifications associées à ce marqueur
      try {
        // Récupérer toutes les notifications non lues ou à venir pour ce marqueur
        const { data: notifications, error: notifError } = await supabase
          .from('notifications')
          .select('*')
          .eq('marker_id', markerId)
          .or('status.eq.unread,reminder_date.gte.' + new Date().toISOString());
          
        if (notifError) {
          logger.error(`Erreur lors de la récupération des notifications pour le marqueur ${markerId}:`, notifError);
        } else if (notifications && notifications.length > 0) {
          logger.info(`Mise à jour de ${notifications.length} notifications suite au changement de catégorie du marqueur ${markerId}`);
          
          // Mettre à jour chaque notification
          for (const notif of notifications) {
            await notificationsService.update(notif.id, {
              markerCategory: newCategoryName
            });
          }
          
          logger.info(`${notifications.length} notifications mises à jour avec la nouvelle catégorie: ${newCategoryName}`);
          
          // Émettre un événement pour mettre à jour l'interface utilisateur sans rechargement
          eventsService.emit('NOTIFICATIONS_CATEGORY_UPDATED', {
            markerId: markerId,
            oldCategoryId: oldCategoryId,
            newCategoryId: newCategoryId,
            newCategoryName: newCategoryName,
            notificationIds: notifications.map(n => n.id)
          });
          
          logger.info('Événement NOTIFICATIONS_CATEGORY_UPDATED émis pour mise à jour de l\'interface');
        } else {
          logger.info(`Aucune notification à mettre à jour pour le marqueur ${markerId}`);
        }
      } catch (notifUpdateError: any) {
        logger.error(`Erreur lors de la mise à jour des notifications pour le marqueur ${markerId}:`, notifUpdateError);
      }

      logger.info(`Changement de catégorie enregistré pour le marqueur ${markerId}: ${oldCategoryId} (${oldCategoryName}) -> ${newCategoryId} (${newCategoryName})`);
    } catch (error: any) {
      logger.error(`Erreur lors de l'enregistrement du changement de catégorie pour le marqueur ${markerId}:`, error);
      // Ne pas propager l'erreur pour ne pas bloquer le flux principal
    }
  },

  /**
   * Met à jour la visibilité d'une catégorie
   * @param categoryId L'ID de la catégorie
   * @param isVisible La nouvelle valeur de visibilité
   * @returns Une promesse qui résout avec la catégorie mise à jour
   */
  async updateVisibility(categoryId: string, isVisible: boolean): Promise<Category> {
    try {
      logger.info(`Mise à jour de la visibilité de la catégorie ${categoryId}: ${isVisible}`);
      
      const { data, error } = await supabase
        .from('categories')
        .update({ is_visible: isVisible })
        .eq('id', categoryId)
        .select()
        .single();

      if (error) {
        logger.error(`Erreur lors de la mise à jour de la visibilité de la catégorie ${categoryId}:`, error);
        throw error;
      }

      if (!data) {
        throw new Error(`Catégorie ${categoryId} non trouvée`);
      }

      logger.info(`Visibilité de la catégorie ${categoryId} mise à jour avec succès`);
      return toAppFormat(data);
    } catch (error) {
      logger.error(`Erreur lors de la mise à jour de la visibilité de la catégorie ${categoryId}:`, error);
      throw error;
    }
  },

  /**
   * Met à jour l'ordre d'affichage de plusieurs catégories (utilise les préférences utilisateur)
   * @param updates Tableau d'objets {id, displayOrder}
   */
  async updateDisplayOrder(updates: Array<{ id: string; displayOrder: number }>): Promise<void> {
    try {
      logger.info(`Mise à jour de l'ordre d'affichage de ${updates.length} catégories`);
      
      // Utiliser le service de préférences utilisateur
      const preferencesUpdates = updates.map(u => ({
        categoryId: u.id,
        displayOrder: u.displayOrder
      }));
      
      await userCategoryPreferencesService.updateDisplayOrders(preferencesUpdates);

      logger.info('Ordre d\'affichage des catégories mis à jour avec succès');
    } catch (error) {
      logger.error('Erreur lors de la mise à jour de l\'ordre d\'affichage des catégories:', error);
      throw error;
    }
  }
};

